import React, {useContext, useEffect, useState} from 'react';
import './NavHeader.scss'
import {
  Header,
  HeaderContainer,
  HeaderGlobalAction,
  HeaderGlobalBar,
  HeaderMenu,
  HeaderMenuButton,
  HeaderMenuItem,
  HeaderName,
  HeaderNavigation,
  HeaderPanel,
  HeaderSideNavItems,
  SideNav,
  SideNavItems,
  SkipToContent,
} from '@carbon/react';
import {
  CloudDownload,
  CloudOffline,
  Logout,
  Notification,
  NotificationNew,
  PhoneOff,
  PhoneOutgoing,
  Recording,
  UserAvatar
} from '@carbon/react/icons';
import {Link, NavLink, useNavigate} from "react-router-dom";
import authContext from "../Auth/AuthContext";
import NotificationPanelList from "../NotificationPanelList";
import logoImg from '../../images/logo.png';
import PropTypes from "prop-types";
import StopRecordingModal from "../StopRecordingModal";

const NavHeader = (props) => {
  const authCtx = useContext(authContext)
  const navigate = useNavigate()
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false)
  const [userNotificationsState, setUserNotificationsState] = useState(0)
  const upConnRecoverer = props.upConnRecoverer
  const cloudConnected = props.upstreamConnected
  const userNotifications = props.userNotifications
  const callsEnabled = props.callsEnabled

  const runningRec = props.runningRec
  const setRecordingRunning = props.setRecordingRunning
  const isRecordingRunning = runningRec != null
  const [recordingModalOpen, setRecordingModalOpen] = useState(false)

  useEffect(() => {
    setUserNotificationsState(userNotifications)
  }, [userNotifications]);

  const notificationReadCallback = (alreadyRead) => {
    if (!alreadyRead) {
      setUserNotificationsState(userNotificationsState - 1)
    }
    setNotificationPanelOpen(false)
  }

  const openRecordingModal = () => {
    if (!runningRec) {
      console.warn("no running recording found")
      return
    }
    setRecordingModalOpen(true)
  }

  return (
    <HeaderContainer render={({isSideNavExpanded, onClickSideNavExpand}) => (
      <Header aria-label="Guardian">
        <StopRecordingModal
          setOpen={setRecordingModalOpen}
          recording={runningRec || {}}
          isOpen={recordingModalOpen}
          updateRecording={setRecordingRunning}
        />
        <SkipToContent/>
        <HeaderMenuButton
          aria-label="Open menu"
          onClick={onClickSideNavExpand}
          isActive={isSideNavExpanded}
        />
        <HeaderName to="/" element={Link} prefix="">
          <img alt="Freddo & Co." src={logoImg} width="100px"/>
        </HeaderName>
        <HeaderNavigation aria-label="Guardian">
          <HeaderMenuItem to="/" element={NavLink}>Dashboard</HeaderMenuItem>
          <HeaderMenu element={NavLink} aria-label="Sistemi" menuLinkName="Sistemi">
            <HeaderMenuItem to="/customers" element={NavLink}>Sistemi</HeaderMenuItem>
            <HeaderMenuItem to="/recordings" element={NavLink}>Rendicontazioni</HeaderMenuItem>
          </HeaderMenu>
          <HeaderMenuItem to="/reports" element={NavLink}>Reports</HeaderMenuItem>
          <HeaderMenu aria-label="Utenti" menuLinkName="Operatori">
            <HeaderMenuItem to="/users/list" element={NavLink}>Operatori</HeaderMenuItem>
            <HeaderMenuItem to="/users/shifts" element={NavLink}>Turni</HeaderMenuItem>
            <HeaderMenuItem to="/users/devices" element={NavLink}>Dispositivi</HeaderMenuItem>
          </HeaderMenu>
          <HeaderMenu aria-label="Impostazioni" menuLinkName="Impostazioni">
            <HeaderMenuItem to="/system/events" element={NavLink}>Log Eventi</HeaderMenuItem>
            <HeaderMenuItem to="/system/calls" element={NavLink}>Centralino</HeaderMenuItem>
            <HeaderMenuItem to="/system/status" element={NavLink}>Stato Sistema</HeaderMenuItem>
          </HeaderMenu>
        </HeaderNavigation>
        <HeaderGlobalBar>
          {
            isRecordingRunning &&
            <HeaderGlobalAction
              aria-label="Rendicontazione attiva"
              onClick={openRecordingModal}
              tooltipAlignment="end"
              className="recording-icon recording">
              <Recording className="recording-blink" size={25}/>
            </HeaderGlobalAction>
          }
          <HeaderGlobalAction
            aria-label={cloudConnected ? "L'interfaccia Guardian sta ricevendo aggiornamenti in tempo reale" : "Il sistema non riceve aggiornamenti real time. Clicca sull'icona per riconnetterti"}
            tooltipAlignment="end"
            className={"nav-cloud-status nav-cloud-status" + (cloudConnected ? "-connected" : "-disconnected")}
            onClick={upConnRecoverer}>
            {cloudConnected ? <CloudDownload size={25}/> : <CloudOffline size={25}/>}
          </HeaderGlobalAction>
          <HeaderGlobalAction
            aria-label={callsEnabled ? "Centralino attivo. Le chiamate vocali in uscita sono in funzione" : "Centralino disattivato. Chiamate vocali in uscita sospese."}
            tooltipAlignment="end"
            className={"nav-calls-status nav-calls-status" + (callsEnabled ? "-enabled" : "-disabled")}
            onClick={() => navigate("/system/calls")}>
            {callsEnabled ? <PhoneOutgoing size={25}/> : <PhoneOff size={25}/>}
          </HeaderGlobalAction>
          <HeaderGlobalAction
            aria-label={userNotificationsState > 0 ? "Hai " + userNotificationsState + " notific" + (userNotificationsState > 1 ? "he" : "a") + " da leggere" : "Notifiche"}
            className={userNotificationsState > 0 ? "nav-user-has-notifications" : ""}
            isActive={notificationPanelOpen}
            onClick={() => {
              setNotificationPanelOpen(!notificationPanelOpen)
            }}>
            {
              userNotificationsState > 0 ? <NotificationNew size={25}/> : <Notification size={25}/>
            }
          </HeaderGlobalAction>
          <Link to="/profile">
            <HeaderGlobalAction aria-label="Profilo">
              <UserAvatar size={25}/>
            </HeaderGlobalAction>
          </Link>
          <HeaderGlobalAction onClick={authCtx.onLogout} aria-label="Esci">
            <Logout size={25}/>
          </HeaderGlobalAction>
          <HeaderPanel aria-label="Header Panel" expanded={notificationPanelOpen}>
            {
              notificationPanelOpen && <NotificationPanelList notificationReadCallback={notificationReadCallback}/>
            }
          </HeaderPanel>
        </HeaderGlobalBar>
        <SideNav
          aria-label="Side navigation"
          expanded={isSideNavExpanded}
          isPersistent={false}>
          <SideNavItems>
            <HeaderSideNavItems>
              <HeaderMenuItem to="/" element={NavLink}>Dashboard</HeaderMenuItem>
              <HeaderMenu element={NavLink} aria-label="Sistemi" menuLinkName="Sistemi">
                <HeaderMenuItem to="/customers" element={NavLink}>Sistemi</HeaderMenuItem>
                <HeaderMenuItem to="/recordings" element={NavLink}>Rendicontazioni</HeaderMenuItem>
              </HeaderMenu>
              <HeaderMenuItem to="/reports" element={NavLink}>Reports</HeaderMenuItem>
              <HeaderMenu aria-label="Utenti" menuLinkName="Operatori">
                <HeaderMenuItem to="/users/list" element={NavLink}>Operatori</HeaderMenuItem>
                <HeaderMenuItem to="/users/shifts" element={NavLink}>Turni</HeaderMenuItem>
                <HeaderMenuItem to="/users/devices" element={NavLink}>Dispositivi</HeaderMenuItem>
              </HeaderMenu>
              <HeaderMenu aria-label="Impostazioni" menuLinkName="Impostazioni">
                <HeaderMenuItem to="/system/events" element={NavLink}>Log Eventi</HeaderMenuItem>
                <HeaderMenuItem to="/system/calls" element={NavLink}>Centralino</HeaderMenuItem>
                <HeaderMenuItem to="/system/status" element={NavLink}>Stato Sistema</HeaderMenuItem>
              </HeaderMenu>
            </HeaderSideNavItems>
          </SideNavItems>
        </SideNav>
      </Header>
    )}/>
  )
}

NavHeader.propTypes = {
  upConnRecoverer: PropTypes.func.isRequired,
  upstreamConnected: PropTypes.bool.isRequired,
  userNotifications: PropTypes.number.isRequired,
  callsEnabled: PropTypes.bool.isRequired,
  runningRec: PropTypes.object,
  setRecordingRunning: PropTypes.func.isRequired,
}

export default NavHeader;